import { calculateUpcomingEventsCutoffDate } from "@/domains/calendar/calculateUpcomingEventsCutoffDate";
import { Uuid } from "@/domains/global/identifiers";
import { AppStore } from "@/store/AppStore";
import { MIN_ATTENDEE_COUNT } from "@/store/calendar/space-account-calendar-event/helpers";
import { IndexedSpaceAccountCalendarEventSyncUpdateValue } from "@/store/calendar/space-account-calendar-event/types";
import { DateTime } from "luxon";

const sortCalendarEvents = (
  events: Array<IndexedSpaceAccountCalendarEventSyncUpdateValue>
): Uuid[] => {
  const sortedEvents = events.sort((a, b) => {
    // 1. Sort by start_time
    const aStartTime = a.model_data?.start_time ?? "";
    const bStartTime = b.model_data?.start_time ?? "";
    if (aStartTime !== bStartTime) {
      return aStartTime.localeCompare(bStartTime);
    }
    // 2. Sort by end_time
    const aEndTime = a.model_data?.end_time ?? "";
    const bEndTime = b.model_data?.end_time ?? "";
    if (aEndTime !== bEndTime) {
      return aEndTime.localeCompare(bEndTime);
    }
    // 3. Sort alphabetically by title (primary_label)
    const aTitle = a.model_data?.primary_label ?? "";
    const bTitle = b.model_data?.primary_label ?? "";
    if (aTitle !== bTitle) {
      return aTitle.localeCompare(bTitle);
    }
    // 4. Sort by id as final tiebreaker
    return a.model_id.localeCompare(b.model_id);
  });

  return sortedEvents.map(event => event.model_id) as Uuid[];
};

/**
 * Searches for calendar events within a time range using the indexed start_time field.
 * This is more efficient than using .filter() which loads all events into memory.
 * Results are sorted by: start_time, end_time, alphabetically by title (primary_label), then id.
 */
export const searchCalendarEventsByTimeRange = async ({
  store,
  startTime,
  endTime,
  minAttendeeCount = MIN_ATTENDEE_COUNT,
}: {
  store: AppStore;
  startTime: string;
  endTime: string;
  minAttendeeCount?: number;
}): Promise<Uuid[]> => {
  const startUtc = DateTime.fromISO(startTime).toUTC().toISO() ?? "";
  const endUtc = DateTime.fromISO(endTime).toUTC().toISO() ?? "";

  const events = await store.spaceAccountCalendarEvents.localTable
    .where("start_time_utc")
    .between(startUtc, endUtc, true, true)
    .and(
      event =>
        (event.model_data?.attendee_count ?? 0) >= minAttendeeCount &&
        event.model_data?.self_response_status !== "DECLINED"
    )
    .toArray();

  return sortCalendarEvents(events);
};

/**
 * Searches for calendar events that are currently ongoing.
 * An event is considered ongoing if the current time is between
 * the event's start_time and end_time (inclusive start, exclusive end).
 * Results are sorted by: start_time, end_time, alphabetically by title (primary_label), then id.
 */
export const searchOngoingCalendarEvents = async ({
  store,
  minAttendeeCount = MIN_ATTENDEE_COUNT,
}: {
  store: AppStore;
  minAttendeeCount?: number;
}): Promise<Uuid[]> => {
  const currentTimeUtc = DateTime.now().toUTC().toISO() ?? "";
  const events = await store.spaceAccountCalendarEvents.localTable
    .where("end_time_utc")
    .above(currentTimeUtc)
    .and(event => {
      return (
        event.start_time_utc < currentTimeUtc &&
        (event.model_data?.attendee_count ?? 0) >= minAttendeeCount &&
        event.model_data?.self_response_status !== "DECLINED"
      );
    })
    .toArray();

  return sortCalendarEvents(events);
};

/**
 * Returns the ongoing and upcoming calendar events.
 * Upcoming events includes all events that are +2 weekdays rounded up to end of day
 * i.e. on Friday, it would show up to Tuesday EOD
 * Exclude events with less than 2 attendees and declined events
 */
export const getOngoingAndUpcomingCalendarEvents = async ({
  store,
  minAttendeeCount = MIN_ATTENDEE_COUNT,
}: {
  store: AppStore;
  minAttendeeCount?: number;
}): Promise<{ ongoingEventIds: Uuid[]; upcomingEventIds: Uuid[] }> => {
  const now = DateTime.now();
  const upcomingMeetingsEnd = calculateUpcomingEventsCutoffDate({ startDate: now });
  const ongoingEventIds = await searchOngoingCalendarEvents({ store, minAttendeeCount });
  const upcomingEventIds = await searchCalendarEventsByTimeRange({
    store,
    startTime: now.toISO() ?? "",
    endTime: upcomingMeetingsEnd.toISO() ?? "",
    minAttendeeCount,
  });

  return { ongoingEventIds, upcomingEventIds };
};
